import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import List from 'semantic-ui-react/dist/commonjs/elements/List';
import Popup from 'semantic-ui-react/dist/commonjs/modules/Popup';
import { destroyUserFlowAction } from '@Redux/actions/user-flow/user-flow-action';
import { toggleNewCouponCode } from '@Redux/actions/transaction';
import { clearDashboardCalculatorData } from '@Redux/actions/dashboard';
import { BASE_CDN_CONSUMER_URL, DASHBOARD_URL, DEVICE_MOBILE } from '../../utils/constants';
import { logEventV2, getPageName } from '../../utils/events/logUserEvents';
import { EventButtonType, EventNamesV2, FLOW_TYPE } from '../../utils/events/eventsConstants';
import { selectUiTriggerStateReducer } from '@Redux/selectors';
import '../header/header.css';
import { AppDispatch } from '../../store.type';
import { useAccountMenuItems } from './hooks/useAccountMenuItems';
import { MenuItem } from './AccountMenuPopup.types';
import { AccountMenuPopupGlobalStyle, LogoutMenuItemContainer } from './AccountMenuPopup.styles';
import { useLogout, LOGOUT_LABEL } from '../../hooks/useLogout';

interface AccountMenuPopupProps {
  children: React.ReactNode;
}

declare global {
  interface Window {
    zE?: (widget: string, action: string, callbackOrData?: any) => void;
  }
}

const AccountMenuPopup = ({ children }: AccountMenuPopupProps) => {
  const [openMenu, setOpenMenu] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const { device } = useSelector(selectUiTriggerStateReducer);
  const menuItems = useAccountMenuItems();
  const isPopupOpen = openMenu && device !== DEVICE_MOBILE;

  const toggleMenu = useCallback(() => setOpenMenu((prev) => !prev), []);

  const logCTEvents = useCallback((ctaName: string, flowType: string) => {
    logEventV2(EventNamesV2.CTA_CLICKED, {
      type: EventButtonType.BUTTON,
      pageName: getPageName(),
      ctaName,
      flowType
    });
  }, []);

  const logout = useLogout();

  const onLinkClick = useCallback(
    (targetUrl?: string) => {
      toggleMenu();
      dispatch(destroyUserFlowAction());
      if (targetUrl === DASHBOARD_URL) {
        dispatch(toggleNewCouponCode(false));
        dispatch(clearDashboardCalculatorData());
      }
    },
    [dispatch, toggleMenu]
  );

  const handleItemClick = useCallback(
    (item: MenuItem) => (e: React.MouseEvent) => {
      if (item.disabled) {
        e.preventDefault();
        return;
      }
      if (item.key === 'home') {
        logCTEvents('Dashboard', FLOW_TYPE.DASHBOARD);
        onLinkClick(DASHBOARD_URL);
      } else {
        onLinkClick();
      }
    },
    [logCTEvents, onLinkClick]
  );

  return (
    <>
      <AccountMenuPopupGlobalStyle />
      <Popup
        id="account-menu-popup"
        on="click"
        position="bottom right"
        className="account-menu-popup"
        open={isPopupOpen}
        onOpen={toggleMenu}
        onClose={toggleMenu}
        trigger={children}
        content={
          isPopupOpen ? (
            <div className="account-pop-up-wrapper">
              <span className="my-account-heading">My account</span>
              <List divided className="pop-up-list" id="pop-up-list-header">
                <List.Item>
                  {menuItems.map((item) => (
                    <List.Content className="list-content" key={item.key}>
                      <div className="icon">
                        <img
                          alt={item.label}
                          src={item.icon}
                          className={item.disabled ? 'disabled' : ''}
                        />
                      </div>
                      <Link
                        to={item.route}
                        id={item.id}
                        className={'head' + (item.disabled ? ' disabled' : '')}
                        onClick={handleItemClick(item)}
                      >
                        {item.label}
                      </Link>
                    </List.Content>
                  ))}
                  <List.Content className="list-content">
                    <div className="icon">
                      <img
                        alt="logout"
                        src={`${BASE_CDN_CONSUMER_URL}web-revamp/logout-icon.svg`}
                      />
                    </div>
                    <LogoutMenuItemContainer className="head" onClick={logout} id="btn-logout">
                      {LOGOUT_LABEL}
                    </LogoutMenuItemContainer>
                  </List.Content>
                </List.Item>
              </List>
            </div>
          ) : null
        }
      />
    </>
  );
};

export default AccountMenuPopup;
